// WebRTC peer management for the PWA (viewer side).
//
// The PWA is always the OFFERER: it creates the RTCPeerConnection, opens a
// data channel labeled "files", and sends the offer through the Go signaling
// server to the desktop host, which answers. ICE servers come from
// GET /api/ice (STUN now, TURN later) with a Google-STUN fallback.
import { IceCandidateForwarder } from './iceCandidatePrefer';
import {
  ICE_PREFER_IPV6,
  IPV4_CANDIDATE_DELAY_MS,
  SRFLX_IPV4_DELAY_MS,
  SRFLX_IPV6_DELAY_MS,
} from './p2pConfig';
import { ICE_SERVERS, XFER_LABEL } from './protocol';
import {
  countCandidate,
  emptyCandidateCounts,
  type IceCandidateTypeCounts,
  type IceConnectionState,
  type IceGatheringState,
  type IcePairDetail,
  type IceRoute,
  type IceStatus,
} from './iceDiagnostics';

export const DATA_CHANNEL_LABEL = 'files';

export interface PeerOptions {
  preferIpv6?: boolean;
  ipv4CandidateDelayMs?: number;
  srflxIpv4DelayMs?: number;
  srflxIpv6DelayMs?: number;
}

/** WebRTC requires sdpMid and sdpMLineIndex to be both set or both absent. */
export function validCandidateInit(c: RTCIceCandidateInit): boolean {
  const hasMid = c.sdpMid != null && String(c.sdpMid).length > 0;
  const hasIndex = c.sdpMLineIndex != null;
  return (hasMid && hasIndex) || (!hasMid && !hasIndex);
}

export type PathKind = 'lan' | 'host' | 'stun' | 'turn' | 'unknown';

function isLanHostAddress(addr: string): boolean {
  if (!addr) return false;
  const bare = addr.split('%')[0];
  if (bare.includes(':')) {
    const lower = bare.toLowerCase();
    if (lower === '::1') return true;
    if (lower.startsWith('fe80:')) return true;
    if (lower.startsWith('fc') || lower.startsWith('fd')) return true;
    return false;
  }
  const parts = bare.split('.').map((p) => Number(p));
  if (parts.length !== 4 || parts.some((n) => Number.isNaN(n))) return false;
  if (parts[0] === 10) return true;
  if (parts[0] === 172 && parts[1] >= 16 && parts[1] <= 31) return true;
  if (parts[0] === 192 && parts[1] === 168) return true;
  if (parts[0] === 127) return true;
  if (parts[0] === 169 && parts[1] === 254) return true;
  return false;
}

/** Rough IPv6 /64 prefix — same prefix on Wi‑Fi usually means LAN (incl. global IPv6 from router). */
function ipv6Prefix64(addr: string): string | null {
  const bare = addr.split('%')[0].toLowerCase();
  if (!bare.includes(':')) return null;
  const parts = bare.split(':').filter((p) => p.length > 0);
  if (parts.length < 4) return null;
  return parts.slice(0, 4).join(':');
}

/** host↔host pair that is on the same LAN (private IPv4, link-local, or same IPv6 /64). */
function isLikelyLanPair(localAddr: string, remoteAddr: string): boolean {
  if (isLanHostAddress(localAddr) || isLanHostAddress(remoteAddr)) return true;
  const lp = ipv6Prefix64(localAddr);
  const rp = ipv6Prefix64(remoteAddr);
  return Boolean(lp && rp && lp === rp);
}

export interface PathDetail {
  path: PathKind;
  ipFamily: 'ipv6' | 'ipv4' | '?';
}

export interface PeerCallbacks {
  /** Local ICE candidates must be forwarded to the host via signaling. */
  onLocalCandidate: (candidate: RTCIceCandidateInit) => void;
  /** Remote description (answer) received from the host. */
  onRemoteAnswer: (sdp: RTCSessionDescriptionInit) => void;
  /** Data channel opened and ready for LIST_FILES. */
  onChannelOpen: (channel: RTCDataChannel) => void;
  /** Channel closed or peer connection failed. */
  onConnectionLost: (reason: string) => void;
  /** ICE diagnostics: gathering/connection state changes and candidate counts. */
  onIceStatus?: (status: IceStatus) => void;
}

export class PeerSession {
  readonly pc: RTCPeerConnection;
  /** Control channel ("files"): JSON/text commands + folder tree. */
  private channel: RTCDataChannel | null = null;
  /** Transfer channel ("xfer"): file bytes + transfer frames. */
  private xfer: RTCDataChannel | null = null;
  private closed = false;
  private readonly iceForward: IceCandidateForwarder;
  /** Live ICE diagnostics state (gathering/connection + candidate counters). */
  private gatheringState: IceGatheringState = 'new';
  private connectionState: IceConnectionState = 'new';
  private readonly candidates: IceCandidateTypeCounts = emptyCandidateCounts();

  constructor(
    private readonly cbs: PeerCallbacks,
    iceServers: RTCIceServer[] = ICE_SERVERS,
    options?: PeerOptions
  ) {
    const preferIpv6 = options?.preferIpv6 ?? ICE_PREFER_IPV6;
    this.iceForward = new IceCandidateForwarder(
      (c) => this.cbs.onLocalCandidate(c),
      preferIpv6,
      {
        ipv4HostMs: options?.ipv4CandidateDelayMs ?? IPV4_CANDIDATE_DELAY_MS,
        srflxIpv4Ms: options?.srflxIpv4DelayMs ?? SRFLX_IPV4_DELAY_MS,
        srflxIpv6Ms: options?.srflxIpv6DelayMs ?? SRFLX_IPV6_DELAY_MS,
      }
    );
    this.pc = new RTCPeerConnection({
      iceServers,
      continualGatheringPolicy: 'gather-continually',
    } as RTCConfiguration);

    this.pc.onicecandidate = (ev: RTCPeerConnectionIceEvent) => {
      if (ev.candidate?.toJSON) {
        const json = ev.candidate.toJSON();
        countCandidate(this.candidates, json.candidate ?? '');
        this.emitIceStatus();
        this.iceForward.handle(json);
      } else {
        this.gatheringState = 'complete';
        this.emitIceStatus();
        this.iceForward.flush();
      }
    };

    this.pc.onicegatheringstatechange = () => {
      const s = this.pc.iceGatheringState;
      if (s === 'gathering' || s === 'complete') this.gatheringState = s;
      this.emitIceStatus();
    };

    this.pc.onconnectionstatechange = () => {
      const s = this.pc.connectionState;
      this.connectionState = s as IceConnectionState;
      this.emitIceStatus();
      if (s === 'connected') this.iceForward.discardHeld();
      if (s === 'failed' || s === 'closed') {
        if (!this.closed) {
          this.closed = true;
          this.cbs.onConnectionLost(s);
        }
      }
    };

    // The channel is created by us before the offer; this handler covers
    // re-negotiation edge cases.
    this.pc.ondatachannel = (ev: RTCDataChannelEvent) => {
      this.attachChannel(ev.channel);
    };
  }

  /** Creates the data channel(s) + offer and returns the local SDP. */
  async createOffer(): Promise<RTCSessionDescriptionInit> {
    const ch = this.pc.createDataChannel(DATA_CHANNEL_LABEL, {
      ordered: true,
    });
    this.attachChannel(ch);
    // Second channel for file bytes: binary frames can never interleave with
    // control JSON, and both stay strictly ordered (SCTP ordered mode).
    const xfer = this.pc.createDataChannel(XFER_LABEL, { ordered: true });
    xfer.binaryType = 'arraybuffer';
    this.attachChannel(xfer);
    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    return { type: offer.type, sdp: offer.sdp };
  }

  /** Applies the host's answer SDP. */
  async applyAnswer(sdp: RTCSessionDescriptionInit): Promise<void> {
    await this.pc.setRemoteDescription(new RTCSessionDescription(sdp));
  }

  /** Adds an ICE candidate relayed by the server. */
  async addCandidate(candidate: RTCIceCandidateInit): Promise<void> {
    if (!candidate.candidate?.trim()) return;
    if (!validCandidateInit(candidate)) return;
    try {
      await this.pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch {
      // Candidates can race the remote description; safe to drop in PoC.
    }
  }

  get xferReady(): boolean {
    return this.xfer?.readyState === 'open';
  }

  /** True when ICE is up and both SCTP channels are open (safe for uploads). */
  get transferReady(): boolean {
    if (this.closed) return false;
    const ice = this.pc.connectionState;
    if (ice !== 'connected') return false;
    return (
      this.channel?.readyState === 'open' &&
      this.xfer?.readyState === 'open'
    );
  }

  /** Sends a text payload over the control channel. */
  send(text: string): boolean {
    if (!this.channel || this.channel.readyState !== 'open') return false;
    this.channel.send(text);
    return true;
  }

  /** Sends a transfer frame (text) or file chunk (ArrayBuffer) on "xfer". */
  sendXfer(data: string | ArrayBuffer): boolean {
    if (!this.xfer || this.xfer.readyState !== 'open') return false;
    // Split by type: RTCDataChannel.send() is overloaded and won't accept
    // the union in one call.
    if (typeof data === 'string') {
      this.xfer.send(data);
    } else {
      this.xfer.send(data);
    }
    return true;
  }

  /** Bytes queued on the xfer channel (upload backpressure). */
  get xferBuffered(): number {
    return this.xfer?.bufferedAmount ?? 0;
  }

  /** Current ICE diagnostics snapshot (gathering/connection state + counters). */
  iceStatus(): IceStatus {
    return {
      gatheringState: this.gatheringState,
      connectionState: this.connectionState,
      candidates: { ...this.candidates },
    };
  }

  /**
   * The candidate pair the browser actually selected (nominated + succeeded).
   * Returns null when ICE never picked a pair - the usual reason for a link
   * that "gathered fine" but never connected.
   */
  async selectedPair(): Promise<IcePairDetail | null> {
    try {
      const stats = await this.pc.getStats();
      let pair: Record<string, unknown> | null = null;
      stats.forEach((report) => {
        if (report.type !== 'candidate-pair') return;
        const r = report as unknown as Record<string, unknown>;
        const succeeded = r.state === 'succeeded' || r.nominated === true;
        if (succeeded && (!pair || r.nominated === true)) {
          pair = r;
        }
      });
      if (!pair) return null;
      const p = pair as unknown as Record<string, unknown>;
      const localId = String(p.localCandidateId);
      const remoteId = String(p.remoteCandidateId);
      return {
        localType: this.candidateType(stats, localId),
        remoteType: this.candidateType(stats, remoteId),
        localAddress: this.candidateAddress(stats, localId),
        remoteAddress: this.candidateAddress(stats, remoteId),
        selected: p.nominated === true || p.state === 'succeeded',
      };
    } catch {
      return null;
    }
  }

  /** Route implied by the selected pair (relay > host/host > srflx > unknown). */
  async describeRoute(): Promise<IceRoute> {
    const pair = await this.selectedPair();
    if (!pair) return 'unknown';
    if (pair.localType === 'relay' || pair.remoteType === 'relay') return 'turn';
    if (pair.localType === 'host' && pair.remoteType === 'host') return 'lan';
    if (pair.localType === 'srflx' || pair.remoteType === 'srflx') return 'stun';
    return 'unknown';
  }

  /**
   * Reports which transport actually carried the connection so the UI can show
   * STUN-vs-TURN truthfully:
   *   'lan'    → host↔host pair (same network, STUN not needed)
   *   'srflx'  → srflx pair (hole-punched direct over the internet)
   *   'turn'   → a relay candidate is in use (STUN-only would have failed)
   *
   * The selected candidate-pair report can lag the channel-open event, so we
   * poll briefly instead of leaving the pill stuck on "Path: ?".
   */
  async describePath(): Promise<PathKind> {
    const d = await this.describePathDetail();
    return d.path;
  }

  async describePathDetail(): Promise<PathDetail> {
    for (let attempt = 0; attempt < 8; attempt++) {
      const detail = await this.probePathDetail();
      if (detail.path !== 'unknown') return detail;
      await new Promise((resolve) => setTimeout(resolve, 400));
      if (this.closed) return { path: 'unknown', ipFamily: '?' };
    }
    return { path: 'unknown', ipFamily: '?' };
  }

  private async probePathDetail(): Promise<PathDetail> {
    try {
      const stats = await this.pc.getStats();
      let pair: Map<string, unknown> | null = null;
      stats.forEach((report) => {
        if (report.type !== 'candidate-pair') return;
        const r = report as unknown as Record<string, unknown>;
        const succeeded = r.state === 'succeeded' || r.nominated === true;
        if (succeeded && (!pair || r.nominated === true)) {
          pair = report as unknown as Map<string, unknown>;
        }
      });
      if (!pair) return { path: 'unknown', ipFamily: '?' };
      const localId = String((pair as any).localCandidateId);
      const remoteId = String((pair as any).remoteCandidateId);
      const localType = this.candidateType(stats, localId);
      const remoteType = this.candidateType(stats, remoteId);
      const localAddr = this.candidateAddress(stats, localId);
      const remoteAddr = this.candidateAddress(stats, remoteId);
      const ipFamily: 'ipv6' | 'ipv4' | '?' =
        localAddr.includes(':') ? 'ipv6' : localAddr.includes('.') ? 'ipv4' : '?';
      if (localType === 'relay' || remoteType === 'relay') return { path: 'turn', ipFamily };
      if (localType === 'host' && remoteType === 'host') {
        const onLan = isLikelyLanPair(localAddr, remoteAddr);
        return { path: onLan ? 'lan' : 'host', ipFamily };
      }
      if (localType === 'srflx' || remoteType === 'srflx') return { path: 'stun', ipFamily };
      return { path: 'unknown', ipFamily };
    } catch {
      return { path: 'unknown', ipFamily: '?' };
    }
  }

  private candidateType(stats: RTCStatsReport, id: string): string {
    let type = '';
    stats.forEach((report) => {
      if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
        if (report.id === id) {
          type = String((report as unknown as Record<string, unknown>).candidateType ?? '');
        }
      }
    });
    return type;
  }

  private candidateAddress(stats: RTCStatsReport, id: string): string {
    let addr = '';
    stats.forEach((report) => {
      if (report.type === 'local-candidate' || report.type === 'remote-candidate') {
        if (report.id === id) {
          const r = report as unknown as Record<string, unknown>;
          addr = String(r.address ?? r.ip ?? '');
        }
      }
    });
    return addr;
  }

  /** Pushes the current ICE diagnostics snapshot to the caller (if subscribed). */
  private emitIceStatus(): void {
    this.cbs.onIceStatus?.(this.iceStatus());
  }

  private attachChannel(ch: RTCDataChannel): void {
    if (ch.label === XFER_LABEL) {
      this.xfer = ch;
      ch.binaryType = 'arraybuffer';
    } else {
      this.channel = ch;
    }
    ch.onopen = () => this.cbs.onChannelOpen(ch);
    // Do not tear down the whole session when one SCTP channel hiccups — that
    // produced false "P2P link failed" banners after a successful upload while
    // the RTCPeerConnection was still up. Rely on pc.connectionState instead.
    ch.onclose = () => this.maybeConnectionLost('channel-closed');
    ch.onerror = () => this.maybeConnectionLost('channel-error');
  }

  /** End the session only when the peer connection itself is dead or both channels are gone. */
  private maybeConnectionLost(reason: string): void {
    if (this.closed) return;
    const pcState = this.pc.connectionState;
    if (pcState === 'failed' || pcState === 'closed') {
      this.closed = true;
      this.cbs.onConnectionLost(pcState === 'failed' ? 'failed' : reason);
      return;
    }
    const controlDead =
      !this.channel || this.channel.readyState === 'closed' || this.channel.readyState === 'closing';
    const xferDead =
      !this.xfer || this.xfer.readyState === 'closed' || this.xfer.readyState === 'closing';
    if (controlDead && xferDead) {
      this.closed = true;
      this.cbs.onConnectionLost(reason);
    }
  }

  close(): void {
    this.closed = true;
    this.iceForward.destroy();
    try {
      this.channel?.close();
    } catch {
      /* already closed */
    }
    try {
      this.xfer?.close();
    } catch {
      /* already closed */
    }
    try {
      this.pc.close();
    } catch {
      /* already closed */
    }
  }
}
